// Shared evidence-provenance vocabulary + tiny presentational helpers.
// Single source of truth so the wizard, report and My Businesses area label evidence identically.
// Rule enforced everywhere: AI-suggested information is NEVER shown as "Verified".
import { Clock } from "lucide-react";

export const PROV = {
  VERIFIED: { label: "Verified", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  PUBLIC_SOURCE: { label: "Public source", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  USER_CONFIRMED: { label: "You confirmed", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  USER_PROVIDED: { label: "You provided", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  AI_SUGGESTED: { label: "AI-suggested", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  UNVERIFIED: { label: "Unverified", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  UNABLE_TO_VERIFY: { label: "Unable to verify", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  NOT_FOUND: { label: "Not found", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  NOT_CONFIGURED: { label: "Not configured", cls: "text-slate-500 bg-slate-100 border-slate-200" },
  CONFIRMED: { label: "Confirmed", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  LIKELY: { label: "Likely", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  UNKNOWN: { label: "Unknown", cls: "text-slate-500 bg-slate-100 border-slate-200" },
};

export function ProvBadge({ status, testid, className = "" }) {
  const p = PROV[status] || PROV.UNVERIFIED;
  return (
    <span data-testid={testid}
      className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.cls} ${className}`}>
      {p.label}
    </span>
  );
}

const STALE = {
  fresh: "text-emerald-700 bg-emerald-50 border-emerald-200",
  recent: "text-emerald-700 bg-emerald-50 border-emerald-200",
  aging: "text-amber-700 bg-amber-50 border-amber-200",
  stale: "text-red-700 bg-red-50 border-red-200",
  unknown: "text-slate-600 bg-slate-100 border-slate-200",
};

export function StalenessPill({ staleness, testid }) {
  if (!staleness) return null;
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STALE[staleness.level] || STALE.unknown}`}>
      <Clock className="w-3 h-3" /> {staleness.label}
    </span>
  );
}

// Verification headline for a saved business (never overstates trust).
export const VERIFICATION = {
  VERIFIED: { label: "Verified", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  AI_SUGGESTED: { label: "AI-assisted", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  UNVERIFIED: { label: "Owner-confirmed", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
};

export function VerificationBadge({ verification, testid }) {
  if (!verification) return null;
  const v = VERIFICATION[verification.status] || VERIFICATION.UNVERIFIED;
  return (
    <span data-testid={testid} title={verification.label}
      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${v.cls}`}>
      {v.label}
    </span>
  );
}

export const FIELD_LABELS = {
  name: "Business name", address: "Address", phone: "Phone", website: "Website",
  category: "Business category", hours: "Hours", business_status: "Business status",
  location: "Location (map)", place_id: "Place identifier",
};

// Human-readable value for a verified field (values may be strings, arrays or geo objects).
export function fieldValueDisplay(field, value) {
  if (value == null || value === "") return null;
  if (field === "hours" && Array.isArray(value)) return value.join(" · ");
  if (field === "location" && typeof value === "object") {
    const lat = value.lat ?? value.latitude;
    const lng = value.lng ?? value.longitude;
    return lat != null && lng != null ? `${lat}, ${lng}` : null;
  }
  return String(value);
}
